import { useState, useCallback, useMemo } from "react";

export interface UseCarouselOptions {
  /** Number of items/slides in the carousel */
  itemCount: number;
  /** Whether navigation wraps around from last to first and vice versa */
  loop?: boolean;
}

export interface UseCarouselReturn {
  /** Current active slide index (0-based) */
  currentIndex: number;
  /** Navigate to a specific slide by index */
  goTo: (index: number) => void;
  /** Navigate to the previous slide */
  goPrev: () => void;
  /** Navigate to the next slide */
  goNext: () => void;
  /** Whether previous navigation is available */
  canGoPrev: boolean;
  /** Whether next navigation is available */
  canGoNext: boolean;
  /** CSS translateX value for the carousel track (e.g., "-200%") */
  translateX: string;
}

/**
 * Shared carousel state management hook.
 * Handles index tracking, boundary clamping, loop wrapping,
 * and translateX calculation for CSS-based carousel transitions.
 */
export function useCarousel({
  itemCount,
  loop = false,
}: UseCarouselOptions): UseCarouselReturn {
  const [currentIndex, setCurrentIndex] = useState(0);

  const maxIndex = Math.max(0, itemCount - 1);

  const goTo = useCallback(
    (index: number) => {
      if (itemCount <= 0) return;

      if (loop) {
        // Wrap around: -1 goes to last, itemCount goes to first
        setCurrentIndex(((index % itemCount) + itemCount) % itemCount);
      } else {
        setCurrentIndex(Math.max(0, Math.min(index, maxIndex)));
      }
    },
    [itemCount, maxIndex, loop]
  );

  const goPrev = useCallback(() => {
    goTo(currentIndex - 1);
  }, [currentIndex, goTo]);

  const goNext = useCallback(() => {
    goTo(currentIndex + 1);
  }, [currentIndex, goTo]);

  const canGoPrev = useMemo(() => {
    if (itemCount <= 0) return false;
    return loop || currentIndex > 0;
  }, [itemCount, currentIndex, loop]);

  const canGoNext = useMemo(() => {
    if (itemCount <= 0) return false;
    return loop || currentIndex < maxIndex;
  }, [itemCount, currentIndex, maxIndex, loop]);

  const translateX = `${-currentIndex * 100}%`;

  return {
    currentIndex,
    goTo,
    goPrev,
    goNext,
    canGoPrev,
    canGoNext,
    translateX,
  };
}
